// Moves units along flow fields toward their goal, with local separation + collision.
// Makes group movement look coherent (no overlap/jitter). This is a "feel" system.
import { TILE, TICK_HZ } from '../../core/constants';
import type { StepContext } from '../context';
import type { BuildingEntity } from '../entity-types';
import type { GameState, UnitEntity, EntityId } from '../types';
import { entitiesSorted, isAlive, getPlayer } from '../queries';
import { steerToGoal, applyVelocityMove, slidePosition, makePathContext, moveTowardGoal } from '../pathing';
import { canUnitGarrison } from '../garrison';
import {
  ensureGarrisonHost,
  getGarrisonHost,
  garrisonedInId,
  isChanneling,
  hasMorph,
  setGarrisonedIn,
} from '../capabilities';
import { sandboxFreezeUnits } from '../sandbox-flags';
import { resolveUnitStat } from '../modifiers';
import { isAirEntity } from '../mobility';

const scratch: EntityId[] = [];
const SEP_BLEND = 0.55;

function separationForPair(e: UnitEntity, other: { id: EntityId; pos: { x: number; y: number }; radius: number }): { x: number; y: number } {
  const ox = e.pos.x - other.pos.x;
  const oy = e.pos.y - other.pos.y;
  const dd = Math.hypot(ox, oy);
  const minDist = e.radius + other.radius;
  if (dd >= minDist) return { x: 0, y: 0 };
  if (dd > 0) {
    const push = (minDist - dd) / minDist;
    return { x: (ox / dd) * push, y: (oy / dd) * push };
  }

  const low = Math.min(e.id, other.id);
  const high = Math.max(e.id, other.id);
  const angle = (((low * 73856093 + high * 19349663) >>> 0) % 360) * (Math.PI / 180);
  const sign = e.id === low ? 1 : -1;
  return { x: Math.cos(angle) * sign, y: Math.sin(angle) * sign };
}

function goalOf(e: UnitEntity): { x: number; y: number } | null {
  const o = e.orders[0];
  if (!o) return null;
  if (o.type === 'move' || o.type === 'attackMove' || o.type === 'moveInOrder') return { x: o.x, y: o.y };
  return null;
}

function removeReservation(building: BuildingEntity, unitId: EntityId): void {
  const host = getGarrisonHost(building);
  if (!host?.garrisonReservedIds) return;
  host.garrisonReservedIds = host.garrisonReservedIds.filter((id) => id !== unitId);
}

export function movementSystem(state: GameState, ctx: StepContext): void {
  if (sandboxFreezeUnits(state)) return;
  const nav = ctx.services.nav;
  const flow = ctx.services.flow;
  const dt = 1 / TICK_HZ;

  for (const e of entitiesSorted(state)) {
    if (e.kind !== 'unit' || !isAlive(e)) continue;
    if (e.state === 'garrisoned' || garrisonedInId(e) !== undefined) {
      e.vel = { x: 0, y: 0 };
      continue;
    }
    if (hasMorph(e) || isChanneling(e)) {
      e.vel = { x: 0, y: 0 };
      continue;
    }
    const order = e.orders[0];
    const udef = ctx.services.registry.unit(e.defId);
    const player = getPlayer(state, e.owner)!;
    let speed = resolveUnitStat(ctx.services.registry, player, udef, 'speed', state.tick, e);
    if (order?.type === 'moveInOrder') speed = order.groupSpeed;

    if (order?.type === 'garrison') {
      const building = state.entities.get(order.buildingId);
      if (!building || building.kind !== 'building' || !isAlive(building) || !canUnitGarrison(ctx.services.registry, e, building)) {
        if (building?.kind === 'building') removeReservation(building, e.id);
        e.orders.shift();
        e.state = 'idle';
        e.vel = { x: 0, y: 0 };
        continue;
      }
      const d = Math.hypot(building.pos.x - e.pos.x, building.pos.y - e.pos.y);
      if (d <= e.radius + building.radius + 8) {
        removeReservation(building, e.id);
        const host = ensureGarrisonHost(building);
        if (!host.garrisonedIds.includes(e.id)) host.garrisonedIds.push(e.id);
        setGarrisonedIn(e, building.id);
        e.pos = { x: building.pos.x, y: building.pos.y };
        e.vel = { x: 0, y: 0 };
        e.orders = [];
        e.state = 'garrisoned';
        e.targetId = undefined;
        continue;
      }
      const pathCtx = makePathContext(nav, flow, state.relations, e.owner, isAirEntity(ctx.services.registry, e));
      moveTowardGoal(pathCtx, e, building.pos, speed, dt);
      continue;
    }

    const goal = goalOf(e);
    if (!goal) {
      e.vel = { x: 0, y: 0 };
      continue;
    }

    const flying = isAirEntity(ctx.services.registry, e);
    const pathCtx = makePathContext(nav, flow, state.relations, e.owner, flying);
    const dx = goal.x - e.pos.x;
    const dy = goal.y - e.pos.y;
    const d = Math.hypot(dx, dy);
    if (d <= TILE * 0.4) {
      e.orders.shift();
      e.vel = { x: 0, y: 0 };
      if (e.orders.length === 0 && e.state === 'moving') e.state = 'idle';
      continue;
    }

    const goalTx = Math.floor(goal.x / TILE);
    const goalTy = Math.floor(goal.y / TILE);
    const block = flying
      ? (tx: number, ty: number) => nav.isBlockedForAir(tx, ty)
      : (tx: number, ty: number) => nav.isBlockedFor(e.owner, tx, ty, state.relations);
    const field = flying ? undefined : flow.getFor(nav, goalTx, goalTy, e.owner, block);
    const steer = steerToGoal(pathCtx, e.pos, goal);

    let moveX = steer.x * speed;
    let moveY = steer.y * speed;

    const neighbors = ctx.services.spatial.queryRadius(e.pos.x, e.pos.y, e.radius * 3, scratch);
    let sepX = 0;
    let sepY = 0;
    for (const nid of neighbors) {
      if (nid === e.id) continue;
      const other = state.entities.get(nid);
      if (!other || other.kind === 'resource_node') continue;
      if (flying !== isAirEntity(ctx.services.registry, other)) continue;
      const sep = separationForPair(e, other);
      sepX += sep.x;
      sepY += sep.y;
    }

    moveX += sepX * speed * SEP_BLEND;
    moveY += sepY * speed * SEP_BLEND;

    if (moveX !== 0 || moveY !== 0) {
      const moved = applyVelocityMove(e, moveX, moveY, pathCtx, dt, field);
      if (moved) e.facing = Math.atan2(moveY, moveX);
      else e.vel = { x: 0, y: 0 };
    } else {
      e.vel = { x: 0, y: 0 };
    }
  }

  for (const e of entitiesSorted(state)) {
    if (e.kind !== 'unit' || !isAlive(e) || e.orders.length > 0 || isChanneling(e) || e.state === 'garrisoned') continue;
    const flying = isAirEntity(ctx.services.registry, e);
    const pathCtx = makePathContext(nav, flow, state.relations, e.owner, flying);
    const neighbors = ctx.services.spatial.queryRadius(e.pos.x, e.pos.y, e.radius * 2.5, scratch);
    let sepX = 0;
    let sepY = 0;
    for (const nid of neighbors) {
      if (nid === e.id) continue;
      const other = state.entities.get(nid);
      if (!other || other.kind !== 'unit') continue;
      if (flying !== isAirEntity(ctx.services.registry, other)) continue;
      const sep = separationForPair(e, other);
      sepX += sep.x;
      sepY += sep.y;
    }
    if (sepX === 0 && sepY === 0) continue;
    const nudge = slidePosition(pathCtx, e.pos.x, e.pos.y, e.pos.x + sepX * 8 * dt, e.pos.y + sepY * 8 * dt);
    if (nudge) {
      e.pos.x = nudge.x;
      e.pos.y = nudge.y;
    }
  }
}
